import { Button } from "@/components/ui/button";
import { PHQ9_ITEMS, PHQ9_OPTIONS } from "./screenerData";
import { ArrowRight, ArrowLeft } from "lucide-react";
import type { Answers } from "./screenerSteps";

export interface Phq9StepProps {
  answers: Answers;
  onAnswer: (val: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export function Phq9Step({ answers, onAnswer, onNext, onBack }: Phq9StepProps) {
  const currentStr = (answers["phq9"] as string) || "";
  const values: number[] = currentStr
    ? currentStr.split(",").map((v) => parseInt(v, 10) || 0)
    : new Array(PHQ9_ITEMS.length).fill(-1);

  const isComplete = values.every((v) => v >= 0);

  const setVal = (idx: number, val: number) => {
    const next = [...values];
    next[idx] = val;
    onAnswer(next.join(","));
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-headings text-lg font-bold text-primary sm:text-xl">
          Over the last 2 weeks, how often have you been bothered by these
          problems?
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Tap one option for each row. There are 9 questions.
        </p>
      </div>

      <div className="space-y-4">
        {PHQ9_ITEMS.map((item, idx) => (
          <div key={idx} className="rounded-xl border border-border p-4">
            <p className="mb-3 text-sm font-medium text-primary">
              {idx + 1}. {item}
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {PHQ9_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVal(idx, parseInt(opt.value, 10))}
                  className={`rounded-lg border-2 px-3 py-2.5 text-center text-xs font-semibold transition-all ${
                    values[idx] === parseInt(opt.value, 10)
                      ? "border-accent bg-accent/10 text-primary"
                      : "border-border text-muted-foreground hover:border-accent/40"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <Button
          type="button"
          onClick={onNext}
          disabled={!isComplete}
          size="lg"
          className="ml-auto bg-gradient-accent font-bold text-accent-foreground shadow-glow hover:opacity-90 disabled:opacity-50"
        >
          Continue
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
